import type { TableBlock, TableRow } from '../../../types/investigator';
import { PROVENANCE_PILL_CLASSES } from '../../../config/colors';

// Tier pill colours come from the shared colors module (ticket 15) —
// inferred is violet, never the purple/green drifts this table carried.
// `estimated`/`unknown` are claim-level values beyond the three tiers.
const PROVENANCE_PILL: Record<string, string> = {
  ...PROVENANCE_PILL_CLASSES,
  estimated: 'bg-amber-900/40 text-amber-400 border-amber-700',
  unknown:   'bg-muted text-muted-foreground border-border',
};

function ProvenancePill({ value }: { value: TableRow['provenance'] }) {
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded border text-xs leading-tight ${PROVENANCE_PILL[value] ?? PROVENANCE_PILL.unknown}`}
    >
      {value}
    </span>
  );
}

interface ClaimsTableProps {
  block: TableBlock;
}

export default function ClaimsTable({ block }: ClaimsTableProps) {
  if (block.rows.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      {block.heading && (
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          {block.heading}
        </h4>
      )}
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-1.5 pr-3 font-medium whitespace-nowrap">When</th>
            <th className="py-1.5 pr-3 font-medium">Event</th>
            <th className="py-1.5 pr-3 font-medium whitespace-nowrap hidden sm:table-cell">Source</th>
            <th className="py-1.5 font-medium whitespace-nowrap">Provenance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {block.rows.map((row, i) => (
            <tr key={`${row.claim_id}-${i}`} className="text-foreground hover:bg-muted/30">
              <td className="py-1.5 pr-3 font-mono text-muted-foreground whitespace-nowrap">{row.when}</td>
              <td className="py-1.5 pr-3 leading-snug">{row.event}</td>
              <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap hidden sm:table-cell">
                {row.source.replace(/View$/, '').replace(/([A-Z])/g, ' $1').trim()}
              </td>
              <td className="py-1.5">
                <ProvenancePill value={row.provenance} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
